import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePermissions } from '../context/PermissionsContext';
import { apiClient } from '../api';
import { formatDateTime } from '../utils/stringUtils';
import { DeleteButton } from './shared/DeleteButton';
import { 
  extractTemplateVariablesFromObject, 
  createTestVariables, 
  getNotifierTestSuccessMessage 
} from '../utils/templateVariables';
import TestNotifierModal from './TestNotifierModal';

interface McpProxy {
  id: string;
  name: string;
  description: string;
  channel_prefix: string;
  base_url: string;
  status: string;
  created_at: string;
}

interface MpxTenant {
  id: string;
  config_id: string;
  name: string;
  description: string;
  api_endpoint: string;
  enabled: boolean;
  created_at: string;
  updated_at: string;
}

interface Integration {
  id: string;
  name: string;
  description: string;
  type: string;
  category?: string;
  configuration: any;
  content: any;
  status: string;
  created_at: string;
}

const IntegrationsPage: React.FC = () => {
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const [mcpProxies, setMcpProxies] = useState<McpProxy[]>([]);
  const [mpxTenants, setMpxTenants] = useState<MpxTenant[]>([]);
  const [integrations, setIntegrations] = useState<Integration[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [testingNotifierId, setTestingNotifierId] = useState<string | null>(null);
  const [showTestModal, setShowTestModal] = useState(false);
  const [testNotifier, setTestNotifier] = useState<Integration | null>(null);
  const [testVariables, setTestVariables] = useState<Record<string, string>>({});
  const [testResult, setTestResult] = useState<{ message: string; isSuccess: boolean } | null>(null);

  // Filter integrations based on search term
  const filteredMcpProxies = mcpProxies.filter(proxy => 
    searchTerm === '' || 
    proxy.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    proxy.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
    proxy.channel_prefix.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredMpxTenants = mpxTenants.filter(tenant => 
    searchTerm === '' || 
    tenant.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    tenant.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
    tenant.api_endpoint.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredIntegrations = integrations.filter(integration => 
    searchTerm === '' || 
    integration.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    integration.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
    integration.type.toLowerCase().includes(searchTerm.toLowerCase())
  );

  useEffect(() => {
    fetchIntegrations();
  }, []);

  const fetchIntegrations = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Fetch all integration types in parallel
      const [mcpResponse, mpxResponse, integrationsResponse] = await Promise.allSettled([
        apiClient.get('/mcp-proxies'),
        apiClient.get('/mpx-tenants').catch(() => ({ data: [] })),
        apiClient.get('/integrations').catch(() => ({ data: [] })) // Gracefully handle if endpoint doesn't exist yet
      ]);
      
      if (mcpResponse.status === 'fulfilled') {
        setMcpProxies(mcpResponse.value.data);
      }
      if (mpxResponse.status === 'fulfilled') {
        setMpxTenants(mpxResponse.value.data);
      }
      if (integrationsResponse.status === 'fulfilled') {
        setIntegrations(integrationsResponse.value.data);
      }
    } catch (error: any) {
      console.error('Error fetching integrations:', error);
      setError(error.message || 'Failed to load integrations');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteMpxTenant = async (e: React.MouseEvent, id: string) => {
    e.stopPropagation();

    try {
      await apiClient.delete(`/mpx-tenants/${id}`);
      await fetchIntegrations();
    } catch (error: any) {
      setError(error.message || 'Failed to delete MPX tenant');
    }
  };

  const handleDeleteMcpProxy = async (e: React.MouseEvent, id: string) => {
    e.stopPropagation();

    try {
      await apiClient.delete(`/mcp-proxies/${id}`);
      setMcpProxies(prev => prev.filter(p => p.id !== id));
    } catch (error: any) {
      console.error('Failed to delete MCP proxy:', error);
      setError(error.response?.data?.message || error.message || 'Failed to delete MCP proxy');
    }
  };

  const handleDeleteIntegration = async (e: React.MouseEvent, id: string) => {
    e.stopPropagation();

    try {
      await apiClient.delete(`/integrations/${id}`);
      setIntegrations(prev => prev.filter(n => n.id !== id));
    } catch (error: any) {
      console.error('Failed to delete integration:', error);
      setError(error.response?.data?.message || error.message || 'Failed to delete integration');
    }
  };

  const handleTestIntegration = async (e: React.MouseEvent, integration: Integration) => {
    e.stopPropagation();
    
    // Extract template variables from content
    const variables = extractTemplateVariablesFromObject(integration.content);
    
    // Clear any previous test results
    setTestResult(null);
    
    // Always show modal to allow user to see test results
    setTestNotifier(integration);
    setTestVariables(createTestVariables(variables));
    setShowTestModal(true);
  };

  const sendTestNotification = async (integration: Integration, variables: Record<string, string>) => {
    // Clear previous test result
    setTestResult(null);
    
    try {
      setTestingNotifierId(integration.id);
      setError(null);
      
      const payload = {
        type: integration.type,
        configuration: integration.configuration,
        content: integration.content,
        variables,
      };

      await apiClient.post('/integrations/test', payload);
      
      // Show success message based on type
      const successMessage = getNotifierTestSuccessMessage(integration.type);
      
      setTestResult({ message: successMessage, isSuccess: true });
    } catch (error: any) {
      console.error('Failed to send test notification:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to send test notification';
      setTestResult({ message: errorMessage, isSuccess: false });
      setError(errorMessage);
    } finally {
      setTestingNotifierId(null);
    }
  };

  const handleTestModalSubmit = () => {
    if (testNotifier) {
      sendTestNotification(testNotifier, testVariables);
    }
  };

  const renderMpxTenantsList = () => (
    <div className="card shadow mb-4">
      <div className="card-header py-3 d-flex justify-content-between align-items-center">
        <h6 className="m-0 font-weight-bold text-primary">
          <i className="fas fa-cloud"></i> MPX Tenants {filteredMpxTenants.length > 0 && (
            <span className="badge badge-primary ms-2" style={{ verticalAlign: 'middle' }}>
              {filteredMpxTenants.length}
            </span>
          )}
        </h6>
        {hasPermission('mpx_tenants.edit') && (
          <button 
            className="btn btn-sm btn-primary" 
            onClick={() => navigate('/mpx-tenants/wizard')}
          >
            <i className="fas fa-plus me-2"></i> Add MPX Tenant
          </button>
        )}
      </div>
      <div className="card-body">
        {filteredMpxTenants.length === 0 ? (
          <div className="text-center text-muted py-4">
            <i className="fas fa-cloud fa-3x mb-3" style={{ opacity: 0.3 }}></i>
            <p>{searchTerm ? 'No MPX Tenants match your filter' : 'No MPX Tenants configured yet'}</p>
          </div>
        ) : (
          <div className="table-responsive">
            <table className="table table-hover table-sm">
              <thead>
                <tr>
                  <th style={{width: '20%'}}>Name</th>
                  <th style={{width: '25%'}}>Description</th>
                  <th style={{width: '25%'}}>API Endpoint</th>
                  <th style={{width: '10%'}}>Status</th>
                  <th style={{width: '12%'}}>Created</th>
                  <th style={{width: '18%', textAlign: 'right'}}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredMpxTenants.map((tenant) => (
                  <tr 
                    key={tenant.id}
                    onClick={() => navigate(`/mpx-tenants/edit/${encodeURIComponent(tenant.id)}`)}
                    style={{ cursor: 'pointer' }}
                  >
                    <td><strong>{tenant.name}</strong></td>
                    <td>{tenant.description}</td>
                    <td><code style={{ fontSize: '0.85rem' }}>{tenant.api_endpoint}</code></td>
                    <td>
                      <span className={`badge badge-${tenant.enabled ? 'success' : 'secondary'}`}>
                        {tenant.enabled ? 'ENABLED' : 'DISABLED'}
                      </span>
                    </td>
                    <td>{formatDateTime(tenant.created_at, true)}</td>
                    <td style={{textAlign: 'right', whiteSpace: 'nowrap'}}>
                      <button
                        className="btn btn-sm btn-primary"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/mpx-tenants/edit/${tenant.id}`);
                        }}
                        title="Edit"
                        style={{marginRight: '10px'}}
                      >
                        <i className="fas fa-edit"></i>
                      </button>
                      <DeleteButton
                        onDelete={() => handleDeleteMpxTenant({} as React.MouseEvent, tenant.id)}
                        className="btn-sm"
                        title="Delete MPX tenant"
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );

  const renderMcpProxiesList = () => (
    <div className="card shadow mb-4">
      <div className="card-header py-3 d-flex justify-content-between align-items-center">
        <h6 className="m-0 font-weight-bold text-primary">
          <i className="fas fa-plug"></i> MCP Proxies {filteredMcpProxies.length > 0 && (
            <span className="badge badge-primary ms-2" style={{ verticalAlign: 'middle' }}>
              {filteredMcpProxies.length}
            </span>
          )}
        </h6>
        {hasPermission('mcp_proxies.edit') && (
          <button 
            className="btn btn-sm btn-primary" 
            onClick={() => navigate('/integrations/mcp-proxies/wizard')}
          >
            <i className="fas fa-plus me-2"></i> Add MCP Proxy
          </button>
        )}
      </div>
      <div className="card-body">
        {filteredMcpProxies.length === 0 ? (
          <div className="text-center text-muted py-4">
            <i className="fas fa-plug fa-3x mb-3" style={{ opacity: 0.3 }}></i>
            <p>{searchTerm ? 'No MCP Proxies match your filter' : 'No MCP Proxies configured yet'}</p>
          </div>
        ) : (
          <div className="table-responsive">
            <table className="table table-hover table-sm">
              <thead>
                <tr>
                  <th style={{width: '20%'}}>Name</th>
                  <th style={{width: '25%'}}>Description</th>
                  <th style={{width: '15%'}}>Channel Prefix</th>
                  <th style={{width: '10%'}}>Status</th>
                  <th style={{width: '12%'}}>Created</th>
                  <th style={{width: '18%', textAlign: 'right'}}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredMcpProxies.map((proxy) => (
                  <tr 
                    key={proxy.id}
                    onClick={() => navigate(`/integrations/mcp-proxies/${proxy.id}`)}
                    style={{ cursor: 'pointer' }}
                  >
                    <td><strong>{proxy.name}</strong></td>
                    <td>{proxy.description}</td>
                    <td><code>{proxy.channel_prefix}</code></td>
                    <td>
                      <span className={`badge badge-${proxy.status === 'active' ? 'success' : 'secondary'}`}>
                        {proxy.status.toUpperCase()}
                      </span>
                    </td> 
                    <td>{formatDateTime(proxy.created_at, true)}</td>
                    <td style={{textAlign: 'right'}}>
                      <button
                        className="btn btn-sm btn-primary me-2"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/integrations/mcp-proxies/${proxy.id}`);
                        }}
                        title="Edit"
                        style={{marginRight: '10px'}}
                      >
                        <i className="fas fa-edit"></i>
                      </button>
                      <DeleteButton
                        onDelete={() => handleDeleteMcpProxy({} as React.MouseEvent, proxy.id)}
                        className="btn-sm"
                        title="Delete MCP proxy"
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );

  const renderIntegrationsList = () => (
    <div className="card shadow mb-4">
      <div className="card-header py-3 d-flex justify-content-between align-items-center">
        <h6 className="m-0 font-weight-bold text-primary">
          <i className="fas fa-bell"></i> Integrations {filteredIntegrations.length > 0 && (
            <span className="badge badge-primary ms-2" style={{ verticalAlign: 'middle' }}>
              {filteredIntegrations.length}
            </span>
          )}
        </h6>
        {hasPermission('mcp_proxies.edit') && (
          <button 
            className="btn btn-sm btn-primary" 
            onClick={() => navigate('/integrations/integrations/wizard')}
          >
            <i className="fas fa-plus me-2"></i> Add Integration
          </button>
        )}
      </div>
      <div className="card-body">
        {filteredIntegrations.length === 0 ? (
          <div className="text-center text-muted py-4">
            <i className="fas fa-bell fa-3x mb-3" style={{ opacity: 0.3 }}></i>
            <p>{searchTerm ? 'No Integrations match your filter' : 'No Integrations configured yet'}</p>
          </div>
        ) : (
          <div className="table-responsive">
            <table className="table table-hover table-sm">
              <thead>
                <tr>
                  <th style={{width: '18%'}}>Name</th>
                  <th style={{width: '22%'}}>Description</th>
                  <th style={{width: '10%'}}>Category</th>
                  <th style={{width: '10%'}}>Type</th>
                  <th style={{width: '10%'}}>Status</th>
                  <th style={{width: '12%'}}>Created</th>
                  <th style={{width: '18%', textAlign: 'right'}}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredIntegrations.map((integration) => (
                  <tr 
                    key={integration.id}
                    onClick={() => navigate(`/integrations/integrations/${integration.id}`)}
                    style={{ cursor: 'pointer' }}
                  >
                    <td><strong>{integration.name}</strong></td>
                    <td>{integration.description}</td>
                    <td>
                      <span className="badge badge-secondary">
                        {(integration.category || 'general').toUpperCase()}
                      </span>
                    </td>
                    <td>
                      <span className={`badge badge-${
                        integration.type === 'email' ? 'primary' :
                        integration.type === 'slack' ? 'info' :
                        integration.type === 'webhook' ? 'warning' :
                        integration.type === 'stream' ? 'info' :
                        'secondary'
                      }`}>
                        {integration.type.toUpperCase()}
                      </span>
                    </td>
                    <td>
                      <span className={`badge badge-${integration.status === 'active' ? 'success' : 'secondary'}`}>
                        {integration.status.toUpperCase()}
                      </span>
                    </td>
                    <td>{formatDateTime(integration.created_at, true)}</td>
                    <td style={{textAlign: 'right'}}>
                      {(integration.type === 'email' || integration.type === 'slack' || integration.type === 'webhook' || integration.type === 'stream') && (
                        <button
                          className="btn btn-sm btn-info me-2"
                          style={{marginLeft: '10px'}}
                          onClick={(e) => handleTestIntegration(e, integration)}
                          disabled={testingNotifierId === integration.id}
                          title="Test"
                        >
                          {testingNotifierId === integration.id ? (
                            <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                          ) : (
                            <i className="fas fa-paper-plane"></i>
                          )}
                        </button>
                      )}
                      <button
                        className="btn btn-sm btn-primary me-2"
                        style={{marginLeft: '10px'}}
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/integrations/integrations/${integration.id}`);
                        }}
                        title="Edit"
                      >
                        <i className="fas fa-edit"></i>
                      </button>
                      <DeleteButton
                        onDelete={() => handleDeleteIntegration({} as React.MouseEvent, integration.id)}
                        className="btn-sm"
                        title="Delete integration"
                        style={{marginLeft: '10px'}}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="container-fluid">
      <div className="d-sm-flex align-items-center justify-content-between mb-4">
        <div>
          <button 
            className="btn btn-sm btn-secondary"
            onClick={() => navigate('/')}
          >
            <i className="fas fa-times"></i> Close
          </button>
          <div style={{ position: 'relative', display: 'inline-block', marginLeft: '0.75rem' }}>
            <i className="fas fa-search" style={{ position: 'absolute', left: '0.75rem', top: '50%', transform: 'translateY(-50%)', color: '#6c757d', fontSize: '0.875rem', pointerEvents: 'none' }}></i>
            <input
              type="text"
              className="form-control"
              placeholder="Filter Integrations..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ width: '240px', paddingLeft: '2.25rem', backgroundColor: 'rgba(255, 255, 255, 0.3)', border: 'none', fontSize: '0.875rem', outline: 'none', boxShadow: 'none' }}
              onFocus={(e) => e.target.style.boxShadow = 'none'}
            />
          </div>
        </div>
      </div>

      {error && (
        <div className="alert alert-danger alert-dismissible fade show" role="alert">
          {error}
          <button type="button" className="close" onClick={() => setError(null)} aria-label="Close">
            <span aria-hidden="true">&times;</span>
          </button>
        </div>
      )}

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
          <div className="spinner-border" role="status" style={{ color: 'rgba(0, 0, 0, 0.5)' }}>
            <span className="visually-hidden"></span>
          </div>
        </div>
      ) : (
        <>
          {renderIntegrationsList()}
          {renderMpxTenantsList()}
          {renderMcpProxiesList()}
        </>
      )}

      {/* Test Integration Variables Modal */}
      <TestNotifierModal
        show={showTestModal && testNotifier !== null}
        integrationName={testNotifier?.name || ''}
        variables={testVariables}
        isTesting={testingNotifierId !== null}
        testResult={testResult}
        onVariableChange={(varName, value) => setTestVariables({
          ...testVariables,
          [varName]: value
        })}
        onCancel={() => {
          setShowTestModal(false);
          setTestResult(null);
        }}
        onTest={handleTestModalSubmit}
      />
    </div>
  );
};

export default IntegrationsPage;
